import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { Twemoji } from 'react-emoji-render';
import { ExternalMedia, InternalMedia, Media, Post } from '@sodamnfoolish/sjc-backend-types/src/api/feed/dto';
import { MediaSource } from '@sodamnfoolish/sjc-backend-types/src/api/feed/media-source';
import { useBackend } from '../Hooks/UseBackend';
import { useUrlParams } from '../Hooks/useUrlParams';
import { Platform } from '../post-interface';

import './Styles/MessageCard.css';
import { ChannelIcon } from './ChannelIcon';

const MessageCard = ({
    post,
    background,
    onImageClick,
    platform,
    linked,
    highlighted,
    username,
    icon,
}: {
    post: Post;
    background: string;
    onImageClick?: (index: number) => void;
    platform: string;

    linked?: boolean;
    highlighted?: boolean;

    username?: string;
    icon?: string;
}) => {
    const { walletAddress } = useUrlParams();
    const navigate = useNavigate();
    const location = useLocation();
    const { getMedia } = useBackend();
    const [imageSrcs, setImageSrcs] = useState<string[]>([]);
    const isOnTokenCreate = location.pathname === '/TokenCreate';
    const isOnResultPage = location.pathname === '/Result';

    useEffect(() => {
        const fetchImages = async () => {
            if (post.media && post.media.length > 0) {
                const mediaPromises = post.media.map((mediaItem: Media) => {
                    if (mediaItem.source === MediaSource.Internal) {
                        return getMedia((mediaItem as InternalMedia).id)
                            .then((blob) => URL.createObjectURL(blob))
                            .catch((error) => {
                                console.error('Error fetching internal media:', error);
                                return null;
                            });
                    } else if (mediaItem.source === MediaSource.External) {
                        return Promise.resolve((mediaItem as ExternalMedia).url);
                    }

                    return undefined;
                });

                const images = await Promise.all(mediaPromises);
                setImageSrcs(images.filter(Boolean) as string[]);
            }
        };

        fetchImages();
    }, [getMedia, post.media]);

    const formatTime = (date: Date) => {
        const day = date.getDate();
        const year = date.getFullYear();
        const hours24 = date.getHours();
        const hours12 = hours24 % 12 || 12;
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const amPm = hours24 >= 12 ? 'PM' : 'AM';
        const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
        const month = months[date.getMonth()];
        return `${hours12}:${minutes} ${amPm} - ${month} ${day}, ${year}`;
    };

    const displayText = (text: string, fullstoryUrl: string | null) => {
        if (!text) return null;

        const emoji = /:(0|1|2|3|4|5|6|7|8|9|10):/g;
        text = text.replaceAll('*', '');

        const fullStoryParts = text.split('Full Story');

        return fullStoryParts.map((fullStoryPart, fsIndex) => {
            const parts = fullStoryPart.split(emoji);

            return parts
                .map((part, index) => {
                    const trimmedPart = part.trim();
                    if (index % 2 !== 0) {
                        return <span key={`${fsIndex}-${index}`}>:{part}:</span>;
                    }

                    return trimmedPart ? (
                        <span key={`${fsIndex}-${index}`}>
                            <Twemoji text={trimmedPart} />
                        </span>
                    ) : null;
                })
                .concat(
                    fsIndex < fullStoryParts.length - 1 && fullstoryUrl ? (
                        <a href={fullstoryUrl} target='_blank' rel='noopener noreferrer' key={`fullstory-${fsIndex}`}>
                            Full Story
                            <p></p>
                        </a>
                    ) : null,
                );
        });
    };

    return (
        <section>
            <div className={`mes-card ${highlighted ? 'highlight' : ''} ${linked ? 'linked' : ''}`} style={{ background }}>
                <div className='username-icon'>
                    {username && (
                        <div className='username-container'>
                            <ChannelIcon icon={icon} />
                            <h3 className='username'>{username}</h3>
                        </div>
                    )}
                </div>
                <div className='info'>
                    <h3>{post.text && displayText(post.text, post.fullStoryUrl)}</h3>
                </div>
                <div className='img-section'>
                    {post.media?.length > 0 && imageSrcs.length > 0 ? (
                        imageSrcs.map((src, index) => (
                            <img
                                key={index}
                                className={`post-img ${imageSrcs.length === 1 ? 'single-img' : 'multiple-img'}`}
                                src={src}
                                alt='Post Img'
                                onClick={() => onImageClick && onImageClick(index)}
                            />
                        ))
                    ) : (
                        <p></p>
                    )}
                </div>
                <div className='bottom-section'>
                    <div className='platf-type'>
                        {(platform !== Platform.Telegram || post.username === 'elonalert') && (
                            <div className='tweet-type'>
                                <span className={`tweet-type-text ${post.tweetType?.toLowerCase() || 'unknown'}`} />
                            </div>
                        )}
                        <div className='dat-platf'>
                            <div className='platf-btn'>
                                <a
                                    href={post.url ? post.url : '#'}
                                    target='_blank'
                                    rel='noreferrer'
                                    onClick={(e) => {
                                        if (!post.url) {
                                            e.preventDefault();
                                            console.log('URL not provided');
                                        }
                                    }}
                                >
                                    <h3>View On {post.username === 'elonalert' ? 'twitter' : platform}</h3>
                                </a>
                            </div>
                            <h3>{formatTime(new Date(post.date))}</h3>
                        </div>
                    </div>
                    {!isOnTokenCreate && !isOnResultPage && (
                        <section className='buttons'>
                            <div className={`link-btn ${post.tweetType === 'tweet' ? 'tweet-button' : ''}`}>
                                <button
                                    className='link'
                                    onClick={() => {
                                        navigate(`TokenCreate?walletAddress=${walletAddress}`, {
                                            state: {
                                                source: 'tweet',
                                                post: post,
                                                background,
                                                platform,
                                                token: {
                                                    telegram: post.platform === Platform.Telegram ? post.url : '',
                                                    twitter: post.platform === Platform.Twitter ? post.url : '',
                                                    website: post.url,
                                                },
                                            },
                                        });
                                        console.log('Navigating with post data:', post);
                                    }}
                                >
                                    <span className='span-link'>&#8250;</span>
                                </button>
                            </div>
                        </section>
                    )}
                </div>
            </div>
        </section>
    );
};

export default MessageCard;
